import { useEffect, useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Plus, X, Trash2, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'

// Fields on a product that can be overridden per card.
const OVERRIDE_FIELDS = [
  ['name',        'Name',                 'text'],
  ['description', 'Description',          'textarea'],
  ['unit',        'Unit',                 'text'],
  ['pack_size',   'Pack size (per box)',  'number'],
  ['price',       'Price (£)',            'number'],
]

// Effective value of a product on a card = override if set, else product field.
function effective(cp, field) {
  if (cp.overrides && Object.prototype.hasOwnProperty.call(cp.overrides, field)) {
    return cp.overrides[field]
  }
  return cp.products?.[field]
}

export default function CardDetail() {
  const { id } = useParams()
  const [card, setCard] = useState(null)
  const [items, setItems] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(null)

  const load = async () => {
    if (isDemoMode) { setCard({ name: 'Demo card' }); return }
    const { data: c } = await supabase
      .from('cards')
      .select('id, name, notes, company_id, companies(id, name)')
      .eq('id', id)
      .maybeSingle()
    setCard(c)

    const { data: cps } = await supabase
      .from('card_products')
      .select('id, product_id, overrides, default_qty, sort_order, products(id, sku, name, description, unit, pack_size, price, image_url)')
      .eq('card_id', id)
      .order('sort_order')
    setItems(cps || [])

    const { data: products } = await supabase
      .from('products')
      .select('id, sku, name, price')
      .is('archived_at', null)
      .order('name')
    setAllProducts(products || [])
  }
  useEffect(() => { load() }, [id])

  const addedIds = useMemo(() => new Set(items.map(i => i.product_id)), [items])
  const candidates = useMemo(
    () => allProducts
      .filter(p => !addedIds.has(p.id))
      .filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.sku.toLowerCase().includes(search.toLowerCase())
      )
      .slice(0, 20),
    [allProducts, addedIds, search]
  )

  const addProduct = async (productId) => {
    if (isDemoMode) return
    const { error } = await supabase.from('card_products').insert({
      card_id: id,
      product_id: productId,
      sort_order: items.length,
    })
    if (error) return alert(error.message)
    setSearch('')
    setAdding(false)
    load()
  }

  const removeItem = async (cpId) => {
    if (isDemoMode) return
    if (!confirm('Remove this product from the card?')) return
    await supabase.from('card_products').delete().eq('id', cpId)
    load()
  }

  const setOverride = async (cpId, field, value) => {
    if (isDemoMode) return
    const cp = items.find(i => i.id === cpId)
    const next = { ...(cp.overrides || {}) }
    if (value === '' || value == null) delete next[field]
    else if (field === 'price') next[field] = Number(value)
    else if (field === 'pack_size') next[field] = parseInt(value, 10) || null
    else next[field] = value
    if (next[field] == null) delete next[field]
    await supabase.from('card_products').update({ overrides: next }).eq('id', cpId)
    setItems(items.map(i => i.id === cpId ? { ...i, overrides: next } : i))
  }

  const clearOverride = async (cpId, field) => {
    if (isDemoMode) return
    const cp = items.find(i => i.id === cpId)
    const next = { ...(cp.overrides || {}) }
    delete next[field]
    await supabase.from('card_products').update({ overrides: next }).eq('id', cpId)
    setItems(items.map(i => i.id === cpId ? { ...i, overrides: next } : i))
  }

  const setDefaultQty = async (cpId, value) => {
    if (isDemoMode) return
    const q = value === '' ? null : Number(value)
    await supabase.from('card_products').update({ default_qty: q }).eq('id', cpId)
    setItems(items.map(i => i.id === cpId ? { ...i, default_qty: q } : i))
  }

  if (!card) return <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>

  return (
    <div className="space-y-4">
      <DemoNotice />

      <Link to={card.company_id ? `/companies/${card.company_id}` : '/companies'} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> {card.companies?.name || 'Back'}
      </Link>

      <div className="card">
        <h3 className="font-semibold text-gray-800">{card.name}</h3>
        {card.companies?.name && <p className="text-sm text-gray-500">{card.companies.name}</p>}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Products on this card</h3>
          <button onClick={() => setAdding(a => !a)} className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>

        {adding && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products to add…"
              className="input-field"
              autoFocus
            />
            {candidates.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 bg-white rounded-lg border border-gray-100 max-h-72 overflow-auto">
                {candidates.map(p => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => addProduct(p.id)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 text-left"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-800">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.sku}</div>
                      </div>
                      {p.price != null && <div className="text-sm text-gray-700">£{Number(p.price).toFixed(2)}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {search && candidates.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">No matching products. Create them on the <Link to="/products" className="text-brand-dark underline">Products</Link> page first.</p>
            )}
          </div>
        )}

        {items.length === 0
          ? <p className="text-sm text-gray-500 py-6 text-center">No products on this card yet.</p>
          : (
            <ul className="divide-y divide-gray-100">
              {items.map(cp => {
                const p = cp.products || {}
                const isOpen = expanded === cp.id
                const overrides = cp.overrides || {}
                const overrideCount = Object.keys(overrides).length
                return (
                  <li key={cp.id} className="py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setExpanded(isOpen ? null : cp.id)} className="text-gray-400 hover:text-gray-700">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {p.image_url
                        ? <img src={p.image_url} alt="" className="w-10 h-10 rounded-md object-cover bg-gray-100" />
                        : <div className="w-10 h-10 rounded-md bg-brand-50" />
                      }
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">
                          {effective(cp, 'name')}
                          {overrides.name !== undefined && <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">override</span>}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {p.sku} · {effective(cp, 'unit') || '—'}
                          {overrideCount > 0 && <span className="ml-2 text-amber-700">{overrideCount} field{overrideCount === 1 ? '' : 's'} overridden</span>}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-700 text-right">
                        {effective(cp, 'price') != null ? `£${Number(effective(cp, 'price')).toFixed(2)}` : '—'}
                        {overrides.price !== undefined && <div className="text-[10px] text-amber-700 uppercase">overridden</div>}
                      </div>
                      <button onClick={() => removeItem(cp.id)} className="text-gray-400 hover:text-red-600 p-1">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {isOpen && (
                      <div className="mt-3 ml-7 p-3 bg-gray-50 rounded-lg space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label>
                            <span className="text-xs text-gray-500 block mb-1">Default order quantity</span>
                            <input
                              type="number"
                              value={cp.default_qty ?? ''}
                              onChange={e => setDefaultQty(cp.id, e.target.value)}
                              placeholder="(blank = no default)"
                              className="input-field"
                            />
                          </label>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Per-card overrides</h4>
                          <p className="text-xs text-gray-500 mb-3">Leave blank to inherit from the master product. A value here overrides it for this card only.</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {OVERRIDE_FIELDS.map(([key, label, type]) => {
                              const isOverridden = overrides[key] !== undefined
                              const value = isOverridden ? overrides[key] : ''
                              const placeholder = p[key] != null && p[key] !== '' ? `Inherits: ${p[key]}` : '(empty)'
                              return (
                                <label key={key} className={type === 'textarea' ? 'sm:col-span-2' : ''}>
                                  <span className="text-xs text-gray-500 block mb-1 flex items-center justify-between">
                                    <span>{label}</span>
                                    {isOverridden && (
                                      <button onClick={() => clearOverride(cp.id, key)} className="text-amber-700 hover:underline inline-flex items-center gap-1">
                                        <RotateCcw className="w-3 h-3" /> reset
                                      </button>
                                    )}
                                  </span>
                                  {type === 'textarea'
                                    ? <textarea
                                        value={value}
                                        onChange={e => setOverride(cp.id, key, e.target.value)}
                                        placeholder={placeholder}
                                        className={`input-field ${isOverridden ? 'border-amber-300 bg-amber-50/30' : ''}`}
                                        rows={2}
                                      />
                                    : <input
                                        type={type}
                                        step={type === 'number' ? '0.01' : undefined}
                                        value={value}
                                        onChange={e => setOverride(cp.id, key, e.target.value)}
                                        placeholder={placeholder}
                                        className={`input-field ${isOverridden ? 'border-amber-300 bg-amber-50/30' : ''}`}
                                      />
                                  }
                                </label>
                              )
                            })}
                          </div>
                        </div>
                        <div className="text-right">
                          <button onClick={() => removeItem(cp.id)} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                            <Trash2 className="w-4 h-4" /> Remove from card
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )
        }
      </div>
    </div>
  )
}
